import { useEffect, useState } from 'react'
import { Bars3Icon, ExclamationCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { Toaster } from 'sonner'
import Sidebar from './Sidebar'

const STORAGE_KEY = 'sidebar'

const TOAST_CLASSES = {
  toast:
    'flex w-full items-center gap-2.5 rounded-xl border border-stone-200 bg-white px-3.5 py-3 text-[13px] text-stone-900 shadow-[0_2px_4px_rgba(28,25,23,0.04),0_18px_44px_-16px_rgba(28,25,23,0.22)]',
  closeButton:
    'order-last ml-auto flex size-6 shrink-0 items-center justify-center rounded-md text-stone-400 transition hover:bg-stone-100 hover:text-stone-700',
}

export default function AppLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(STORAGE_KEY) === 'collapsed')
  const [peeking, setPeeking] = useState(false)
  const [drawerKey, setDrawerKey] = useState<string | null>(null)
  const drawerOpen = drawerKey === location.key
  const expanded = drawerOpen || !collapsed || peeking

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, collapsed ? 'collapsed' : 'expanded')
  }, [collapsed])

  useEffect(() => {
    function handleShortcut(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setDrawerKey(null)
        return
      }
      if (!event.metaKey && !event.ctrlKey) return
      if (event.key === 'b') {
        event.preventDefault()
        setCollapsed((value) => !value)
        setPeeking(false)
      }
      if (event.key === 'k') {
        event.preventDefault()
        navigate('/')
      }
    }

    window.addEventListener('keydown', handleShortcut)
    return () => window.removeEventListener('keydown', handleShortcut)
  }, [navigate])

  function toggle() {
    if (drawerOpen) {
      setDrawerKey(null)
      return
    }
    setCollapsed((value) => !value)
    setPeeking(false)
  }

  const backdrop = drawerOpen
    ? 'opacity-20'
    : `pointer-events-none ${peeking ? 'opacity-5' : 'opacity-0'}`

  return (
    <div className="min-h-dvh bg-white">
      <aside
        onPointerEnter={(event) => setPeeking(event.pointerType === 'mouse' && collapsed && !drawerOpen)}
        onPointerLeave={() => setPeeking(false)}
        className={`fixed inset-y-0 left-0 z-40 overflow-hidden bg-white transition-[width,translate,box-shadow] duration-200 ease-out ${
          expanded ? 'w-60' : 'w-15'
        } ${drawerOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'} ${
          drawerOpen || peeking ? 'shadow-[8px_0_32px_-12px_rgba(28,25,23,0.18)]' : ''
        }`}
      >
        <Sidebar expanded={expanded} onToggle={toggle} />
      </aside>

      <div
        onClick={() => setDrawerKey(null)}
        className={`fixed inset-0 z-30 bg-black transition-opacity duration-200 ${backdrop}`}
      />

      <main
        className={`flex min-h-dvh flex-col transition-[padding] duration-200 ease-out md:py-2 md:pr-2 ${
          collapsed ? 'md:pl-15' : 'md:pl-60'
        }`}
      >
        <header className="sticky top-0 z-20 flex h-12 items-center gap-1 border-b border-stone-200 bg-white px-1.5 md:hidden">
          <button
            type="button"
            aria-label="Open sidebar"
            data-tooltip="Open sidebar"
            onClick={() => setDrawerKey(location.key)}
            className="flex size-10 items-center justify-center rounded-lg text-stone-600 transition-colors hover:bg-black/5"
          >
            <Bars3Icon className="size-5" />
          </button>
          <span className="text-sm text-stone-950">Adlovin</span>
        </header>

        <div className="flex flex-1 flex-col bg-stone-100 md:overflow-hidden md:rounded-2xl md:border-[0.5px] md:border-stone-300">
          <Outlet />
        </div>
      </main>

      <Toaster
        position="top-center"
        closeButton
        icons={{
          info: <InformationCircleIcon className="size-5 shrink-0 text-purple-700" />,
          error: <ExclamationCircleIcon className="size-5 shrink-0 text-red-600" />,
        }}
        style={{ zIndex: 50, fontFamily: 'inherit' }}
        toastOptions={{ unstyled: true, classNames: TOAST_CLASSES }}
      />
    </div>
  )
}
